// [2026-09-02] ADDED — the STAFF admin client for proctoring-sdk-backend's /admin/v1.
//
// WHY A SECOND API MODULE IN THIS REPO, rather than reusing the client package's `api`.
// That one talks to the OLD proctoring-backend gateway (`../proctoring-client-frontend/src/api.js`,
// BASE '/admin/v1' on port 8011) whose response shapes are different and whose service the team has
// since stopped using. This module targets proctoring-sdk-backend, which is the live one. The old
// module and the three screens built on it are NOT removed — they are a different deployable's
// history, and deleting them would throw away the only record of those shapes.
//
// SAME-ORIGIN BY DEFAULT. In production the SDK backend serves this build itself at /admin with its
// assets at /admin-static (app/modules/admin/static.py), so every path below is relative and the
// staff session never crosses an origin boundary — no CORS to configure, no allow-list to widen
// during an incident and forget to narrow. In `npm run dev`, vite proxies /admin/v1 to the backend.
//
// THE SESSION LIVES IN sessionStorage, NOT localStorage. A staff session can read and write EVERY
// tenant's data; leaving it readable on a shared machine after the tab closes is a worse trade than
// making someone sign in again.

// [2026-09-02] CHANGED — the API host is configurable, because this app is now deployed SEPARATELY
// from the backend and its calls are therefore cross-origin.
//
// OLD: const BASE = '/admin/v1'
//
// A relative path only works when the same server hands out both the page and the API. That was
// true while proctoring-sdk-backend served this build at /admin; it is not true for an nginx
// container on its own host, where `/admin/v1/...` would resolve against the STATIC server and
// return index.html with a 200 — a fetch() then fails with "Unexpected token '<'", which names
// neither the real cause nor the missing configuration.
//
// VITE_API_BASE IS BAKED IN AT BUILD TIME, not read at runtime. Vite substitutes import.meta.env at
// compile, so each environment needs its own build — that is the tradeoff of a static bundle, and
// it is why the value belongs in the CI job that builds the image, not in a ConfigMap.
//
//   VITE_API_BASE=https://dev.v1.proctoring-sdk-backend.incruiter.com
//
// Left UNSET it falls back to the relative path, so the same source still works unchanged when the
// backend serves this build itself. The trailing slash is stripped so both forms of the value work.
const API_HOST = (import.meta.env?.VITE_API_BASE || '').replace(/\/+$/, '')
const BASE = `${API_HOST}/admin/v1`
const TOKEN_KEY = 'psdk.admin.session'

export function getToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function setToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch { /* private browsing — works, just not across reloads */ }
}

/** Thrown for every non-2xx. Carries the API's stable `code` so callers branch on it, not on text. */
export class AdminApiError extends Error {
  constructor(message, { code, status, details } = {}) {
    super(message)
    this.name = 'AdminApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

const qs = (params) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== '') search.set(key, value)
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

async function request(path, { method = 'GET', body, signal } = {}) {
  const headers = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body) headers['Content-Type'] = 'application/json'

  let response
  try {
    response = await fetch(BASE + path, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      // The backend also sets a session cookie; sending it keeps a reload working even if
      // sessionStorage was cleared by the browser.
      credentials: 'include',
      signal,
    })
  } catch (err) {
    // A dead network and a 500 are different problems with different fixes, so they get different
    // messages. "Failed to fetch" on its own sends people to check the wrong thing.
    if (err.name === 'AbortError') throw err
    throw new AdminApiError('Could not reach the proctoring service.', { code: 'NETWORK' })
  }

  let payload = null
  const text = await response.text()
  if (text) { try { payload = JSON.parse(text) } catch { /* non-JSON, handled below */ } }

  if (response.status === 401) {
    // The session is gone or expired. Clear it so the shell drops straight to the sign-in screen
    // instead of every panel rendering its own "unauthorised" error.
    setToken(null)
    throw new AdminApiError(payload?.error?.message || 'Your session has expired. Please sign in again.',
                            { code: 'ADMIN_NOT_AUTHENTICATED', status: 401 })
  }
  if (response.status === 403) {
    // DISTINCT FROM 401 ON PURPOSE: signed in, but this account's role may not do this. Dropping to
    // the login screen here would be a lie — signing in again changes nothing.
    throw new AdminApiError(payload?.error?.message || 'Your role does not allow that.',
                            { code: payload?.error?.code || 'FORBIDDEN', status: 403 })
  }
  if (!response.ok) {
    const error = payload?.error || {}
    throw new AdminApiError(error.message || `Request failed (${response.status}).`,
                            { code: error.code, status: response.status, details: error.details })
  }
  // Every endpoint returns the standard envelope { success, request_id, data }.
  return payload?.data ?? payload
}

export const admin = {
  // ── auth ────────────────────────────────────────────────────────────────
  login: (email, password) => request('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/auth/me'),
  changePassword: (current_password, new_password) =>
    request('/auth/password', { method: 'POST', body: { current_password, new_password } }),

  // ── platform ────────────────────────────────────────────────────────────
  overview: (days) => request(`/overview${qs({ days })}`),
  analytics: (days, client_id) => request(`/analytics${qs({ days, client_id })}`),
  health: () => request('/health'),

  // ── tenants ─────────────────────────────────────────────────────────────
  clients: (days) => request(`/clients${qs({ days })}`),
  client: (id) => request(`/clients/${encodeURIComponent(id)}`),
  createClient: (body) => request('/clients', { method: 'POST', body }),
  // Suspend / reactivate. The backend accepts `status`; `reason` is written to the audit log.
  setClientStatus: (id, status, reason) =>
    request(`/clients/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status, reason } }),

  // [2026-09-07] ADDED — turn a tenant's products on and off.
  //
  // ONE PRODUCT PER CALL, and the SERVER applies the dependency rule: enabling ai_proctoring
  // enables sdk with it, disabling sdk disables ai_proctoring. So the UI never has to know the
  // rule and cannot produce the proctoring-only combination the company does not sell. The
  // response carries the resulting `services`, which is what the screen re-renders from — never
  // the optimistic value the button assumed.
  setClientProduct: (id, product, enabled) =>
    request(`/clients/${encodeURIComponent(id)}/products`,
            { method: 'PATCH', body: { product, enabled } }),

  // ── credentials ─────────────────────────────────────────────────────────
  clientKeys: (id) => request(`/clients/${encodeURIComponent(id)}/keys`),
  // The ONLY call whose response contains a live secret. It is rendered once and never stored —
  // do not add it to any cache, retry or logging wrapper.
  // [2026-09-23] CHANGED — sends `service` ('sdk' | 'proctoring'). Defaults to 'sdk', which is
  // exactly what the server assumed when the field was absent, so a caller that passes two
  // arguments gets the same key it always did.
  // OLD: issueKey: (id, label) =>
  // OLD:   request(`/clients/${encodeURIComponent(id)}/keys`, { method: 'POST', body: { label } }),
  issueKey: (id, label, service = 'sdk') =>
    request(`/clients/${encodeURIComponent(id)}/keys`,
            { method: 'POST', body: { label, service } }),
  revokeKey: (id, api_key, reason) =>
    request(`/clients/${encodeURIComponent(id)}/keys/revoke`,
            { method: 'POST', body: { api_key, reason } }),

  // ── money ───────────────────────────────────────────────────────────────
  // NOTE WHAT grant DOES NOT ACCEPT: a client_id the browser invented. The backend now refuses a
  // grant to a tenant that does not exist (a mistyped id used to create orphan money), so a typo
  // here comes back as a 404 rather than a silent success.
  billing: (days) => request(`/credits${qs({ days })}`),
  ledger: (limit) => request(`/credits/ledger${qs({ limit })}`),
  clientCredits: (id, limit) => request(`/credits/${encodeURIComponent(id)}${qs({ limit })}`),
  grant: (client_id, credits, reason, idempotency_key) =>
    request('/credits/grant', { method: 'POST', body: { client_id, credits, reason, idempotency_key } }),
  // The only way to REMOVE credits — a visibly different operation with its own audit line, rather
  // than a negative grant nobody notices in a list.
  adjust: (client_id, credits, reason) =>
    request('/credits/adjust', { method: 'POST', body: { client_id, credits, reason } }),
  reconcile: (id) => request(`/credits/${encodeURIComponent(id)}/reconcile`, { method: 'POST' }),

  // ── feeds ───────────────────────────────────────────────────────────────
  requests: (params) => request(`/requests${qs(params)}`),
  sessions: (params) => request(`/sessions${qs(params)}`),
  session: (id) => request(`/sessions/${encodeURIComponent(id)}`),
  audit: (params) => request(`/audit${qs(params)}`),

  // [2026-09-07] ADDED — the analysis API's jobs, across every tenant.
  //
  // The staff counterpart to the customer's /me/video-jobs. Same three shapes: the feed, one job in
  // full, and the finished report. The report comes WITHOUT its proof images — a real one is 4.3 MB
  // and nearly all of that is one base64 JPEG per flagged interval; each frame is fetched on demand
  // from `videoProofUrl` below, which is a plain <img src>, not a JSON call.
  videoJobs: (params) => request(`/video-jobs${qs(params)}`),
  videoJob: (id) => request(`/video-jobs/${encodeURIComponent(id)}`),
  videoReport: (id) => request(`/video-jobs/${encodeURIComponent(id)}/report`),

  // ── staff accounts (superadmin) ──────────────────────────────────────────
  users: () => request('/users'),
  createUser: (body) => request('/users', { method: 'POST', body }),
  updateUser: (id, body) => request(`/users/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
}

// [2026-09-03] ADDED — fetch ONE evidence screenshot as an object URL.
//
// NOT `request()` ABOVE, because that one parses the body as JSON. This endpoint answers with raw
// image bytes, so it needs its own small path — but it sends exactly the same credentials, so an
// expired session fails here the same way it fails everywhere else.
//
// WHY NOT JUST PUT THE URL IN <img src>. Two independent reasons, either one sufficient:
//   1. This dashboard authenticates with a Bearer token from sessionStorage, and an <img> tag
//      cannot send an Authorization header. The image would 401 into a broken-image icon.
//   2. Making the URL work without a header would mean an unauthenticated link to a screenshot of
//      a candidate's screen — one that keeps working for anyone who ever sees it.
// `credentials: 'include'` matches `request()` and is what carries the session cookie on the
// cross-origin deploy this app runs in (VITE_API_BASE pointing at another host).
//
// THE CALLER MUST REVOKE IT. Each object URL pins its decoded image in memory until
// revokeObjectURL is called; a session with sixty frames would otherwise hold sixty decoded JPEGs
// for the life of the page. EvidenceFrame in views/Feeds.jsx does this in its effect cleanup.
export async function adminEvidenceObjectUrl(sessionId, eventId, signal) {
  const headers = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(
    `${BASE}/sessions/${encodeURIComponent(sessionId)}/evidence/${encodeURIComponent(eventId)}`,
    { headers, credentials: 'include', signal },
  )
  if (!response.ok) throw new Error(`Evidence unavailable (${response.status})`)
  return URL.createObjectURL(await response.blob())
}


// [2026-09-07] ADDED — one proof frame from an analysis report, as an object URL.
//
// A SEPARATE FUNCTION RATHER THAN A PARAMETER ON THE ONE ABOVE. They fetch different routes with
// different path shapes, and the browser-evidence route is addressed by event_id while this one is
// addressed by (kind, index) into the report. Folding both into one function would mean a caller
// choosing between two argument sets, which is how the wrong URL gets built.
//
// The abort/revoke contract is identical and matters for the same reason: a reviewer clicking
// through twenty reports would otherwise pin every decoded frame of all of them in memory for the
// life of the page.
export async function adminProofObjectUrl(jobId, kind, index, signal) {
  const headers = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(
    `${BASE}/video-jobs/${encodeURIComponent(jobId)}/proof/${encodeURIComponent(kind)}/${encodeURIComponent(index)}`,
    { headers, credentials: 'include', signal },
  )
  if (!response.ok) throw new Error(`Proof image unavailable (${response.status})`)
  return URL.createObjectURL(await response.blob())
}
